import React, { useEffect, useMemo, useState } from 'react';
import { getVisitorStats, getRecentVisits } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  BarChart3,
  MapPin,
  Globe,
  Users,
  Laptop
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const VisitorsAnalytics = () => {
  const { t, locale } = useLanguage();
  const [stats, setStats] = useState(null);
  const [recentVisits, setRecentVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(30);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, recentData] = await Promise.all([
          getVisitorStats(range),
          getRecentVisits(20)
        ]);
        setStats(statsData);
        setRecentVisits(recentData);
      } catch (error) {
        console.error('Failed to fetch visitor analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [range]);

  const maxCount = useMemo(() => {
    if (!stats?.daily_visits?.length) return 1;
    return Math.max(...stats.daily_visits.map((item) => item.count));
  }, [stats]);

  const rangeLabel = range === 7 ? t('portalUi.admin.visitors.range7', '7 dienas') : range === 30 ? t('portalUi.admin.visitors.range30', '30 dienas') : t('portalUi.admin.visitors.rangeOther', '{count} dienas').replace('{count}', range);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-visitors">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2E2E2E] flex items-center gap-2" data-testid="admin-visitors-title">
            <BarChart3 className="w-6 h-6 text-[#1F3D2B]" />
            {t('portalUi.admin.visitors.title', 'Apmeklējumu statistika')}
          </h1>
          <p className="text-[#2E2E2E]/60" data-testid="admin-visitors-subtitle">{t('portalUi.admin.visitors.subtitle', 'Publiskās mājaslapas apmeklējumi')}</p>
        </div>
        <div className="flex gap-2">
          {[7, 30].map((option) => (
            <Button
              key={option}
              variant={range === option ? 'default' : 'outline'}
              size="sm"
              onClick={() => setRange(option)}
              className={range === option ? 'bg-[#1F3D2B]' : ''}
              data-testid={`admin-visitors-range-${option}`}
            >
              {option === 7 ? t('portalUi.admin.visitors.range7Short', '7 dienas') : t('portalUi.admin.visitors.range30Short', '30 dienas')}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm" data-testid="admin-visitors-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.visitors.totalLabel', 'Kopā apmeklējumi')}</p>
                <p className="text-3xl font-bold text-[#2E2E2E]">{stats?.total_visits || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center">
                <Globe className="w-6 h-6 text-[#1F3D2B]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="admin-visitors-unique">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.visitors.uniqueLabel', 'Unikālie apmeklētāji')}</p>
                <p className="text-3xl font-bold text-[#2E2E2E]">{stats?.unique_visitors || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm" data-testid="admin-visitors-range">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#2E2E2E]/60">{t('portalUi.admin.visitors.periodLabel', 'Periods')}</p>
                <p className="text-3xl font-bold text-[#2E2E2E]">{rangeLabel}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm" data-testid="admin-visitors-daily">
        <CardHeader>
          <CardTitle>{t('portalUi.admin.visitors.dailyTitle', 'Dienu apmeklējumi')}</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.daily_visits?.length ? (
            <div className="space-y-3">
              {stats.daily_visits.map((item) => (
                <div key={item.date} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-[#2E2E2E]/60">{item.date}</div>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#1F3D2B] rounded-full"
                      style={{ width: `${(item.count / maxCount) * 100}%` }}
                    />
                  </div>
                  <div className="w-12 text-sm text-[#2E2E2E] font-medium" data-testid={`admin-visitors-day-${item.date}`}>{item.count}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-[#2E2E2E]/60">
              {t('portalUi.admin.visitors.dailyEmpty', 'Nav apmeklējumu datu izvēlētajā periodā.')}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm" data-testid="admin-visitors-recent">
        <CardHeader>
          <CardTitle>{t('portalUi.admin.visitors.recentTitle', 'Pēdējie apmeklējumi')}</CardTitle>
        </CardHeader>
        <CardContent>
          {recentVisits.length ? (
            <div className="space-y-3">
              {recentVisits.map((visit) => (
                <div key={visit.id} className="flex items-center justify-between p-3 rounded-xl bg-[#f8faf8]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#1F3D2B]/10 flex items-center justify-center">
                      <Laptop className="w-5 h-5 text-[#1F3D2B]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#2E2E2E]" data-testid={`admin-visit-ip-${visit.id}`}>{visit.ip_address}</p>
                      <p className="text-xs text-[#2E2E2E]/60 flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        {visit.region || t('portalUi.admin.visitors.unknownRegion', 'Nezināms reģions')} {visit.country ? `(${visit.country})` : ''}
                      </p>
                      <p className="text-xs text-[#2E2E2E]/50" data-testid={`admin-visit-count-${visit.id}`}>
                        {t('portalUi.admin.visitors.visitNumber', 'Apmeklējums #{number}').replace('{number}', visit.visit_number)}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-[#2E2E2E]/50">{new Date(visit.created_at).toLocaleString(locale)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-[#2E2E2E]/60">
              {t('portalUi.admin.visitors.recentEmpty', 'Nav apmeklējumu ierakstu.')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VisitorsAnalytics;
